import { useTranslation } from "react-i18next";
import { type BoardBasis, type ExtraService, type TripDto } from "@tc/shared";
import { TransportIcons } from "./TripCard";

/** Enum → i18n key maps shared by the Overview tab and the request summary card. */
export const TRANSPORT_LABEL: Record<"flight" | "train" | "coach" | "hireCar", string> = {
  flight: "request.fFlight",
  train: "request.fTrain",
  coach: "request.fCoach",
  hireCar: "request.fHireCar",
};
export const BOARD_LABEL: Record<BoardBasis, string> = {
  RO: "request.boardRo",
  BB: "request.boardBb",
  HB: "request.boardHb",
  FB: "request.boardFb",
  AI: "request.boardAi",
};
export const EXTRA_LABEL: Record<ExtraService, string> = {
  meetingRoom: "request.xMeeting",
  projector: "request.xProjector",
  massageTable: "request.xMassage",
  iceRecovery: "request.xIce",
  laundry: "request.xLaundry",
  meals: "request.xMeals",
};

/**
 * Read-only request summary shown at the top of the back-office one-page build-proposal view —
 * the club's enquiry at a glance (pax, transport, board, baggage, extras, hotel preferences) so
 * the operator prices against the request without leaving the page.
 */
export function RequestSummaryCard({ trip }: { trip: TripDto }) {
  const { t } = useTranslation();
  const transports = trip.transportModes
    ? (["flight", "train", "coach", "hireCar"] as const).filter((m) => trip.transportModes?.[m])
    : [];
  const extras = trip.extraServices ?? [];
  // Total pax, with the players/staff split in brackets when the breakdown was captured.
  const paxLabel =
    trip.paxCount == null
      ? "—"
      : trip.playerCount != null && trip.staffCount != null
        ? `${trip.paxCount} (${trip.playerCount} ${t("request.fPlayers").toLowerCase()}, ${trip.staffCount} ${t("request.fStaff").toLowerCase()})`
        : String(trip.paxCount);

  return (
    <div className="req-summary">
      <h4>{t("builder.reqSummary")}</h4>
      <div className="kv-grid">
        <div className="kv">
          <span className="k">{t("trips.colPax")}</span>
          <span className="v">{paxLabel}</span>
        </div>
        <div className="kv">
          <span className="k">{t("request.fTransport")}</span>
          <span className="v">
            {transports.length > 0 ? (
              <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <TransportIcons modes={trip.transportModes} />
                {transports.map((m) => t(TRANSPORT_LABEL[m])).join(", ")}
              </span>
            ) : (
              "—"
            )}
          </span>
        </div>
        <div className="kv">
          <span className="k">{t("request.fBoard")}</span>
          <span className="v">{trip.boardBasis ? t(BOARD_LABEL[trip.boardBasis]) : "—"}</span>
        </div>
        <div className="kv">
          <span className="k">{t("request.fBaggage")}</span>
          <span className="v">{trip.baggageRequirements?.notes || "—"}</span>
        </div>
        {trip.hotelPreferenceNotes && (
          <div className="kv">
            <span className="k">{t("request.fHotelPref")}</span>
            <span className="v" style={{ fontStyle: "italic", fontWeight: 400 }}>
              {`“${trip.hotelPreferenceNotes}”`}
            </span>
          </div>
        )}
        <div className="kv">
          <span className="k">{t("request.fExtras")}</span>
          <span className="v">
            {extras.length > 0 ? extras.map((x) => t(EXTRA_LABEL[x])).join(", ") : "—"}
          </span>
        </div>
      </div>
    </div>
  );
}
